/**
 * Turning a METAR sky condition into a density threshold — the number that decides where cloud is.
 *
 * ## The bug this replaces
 *
 * The raymarch cut the shaped density field against `1 − localCoverage`, sweeping a threshold
 * across the whole of 0…1. The field it was cutting does not live there. Measured over a 35 km
 * square of the shipped volume, the `shaped` field's percentiles are:
 *
 *     p2 0.662 · p10 0.708 · p25 0.742 · p50 0.770 · p75 0.802 · p90 0.829 · p98 0.865
 *
 * — a near-Gaussian centred on 0.770 with σ ≈ 0.047, spanning barely a fifth of the range the
 * threshold was moving across. So the threshold was almost always outside the distribution
 * entirely: at SCT it worked out to 0.435, below the *second percentile*, and at BKN to 0.037.
 * Everything passed. Straight-up sky transmittance came out at 0.04–0.14 for scattered and
 * 0.002–0.008 for broken: solid lids with no gaps anywhere, when SCT is three to four oktas and
 * should be mostly open sky. It was invisible only because the shipped default is `few`.
 *
 * ## What an okta actually is, and why the mapping is not the identity
 *
 * `world/weather.ts` states the subtlety and nothing acted on it: oktas measure the fraction of
 * the **sky dome** covered as seen from the ground, not the fraction of the **horizontal plane**
 * occupied by cloud. Those are very different numbers, because cloud near the horizon is seen
 * obliquely and stacks up.
 *
 * The relationship is derivable rather than a matter of taste. A layer covering a fraction `f` of
 * the plane, looked at through a zenith angle `θ`, presents a longer path — `sec θ` times longer —
 * so the chance of a ray missing every cloud is `(1 − f)^{sec θ}`. Averaging what is left over the
 * hemisphere, weighted by solid angle:
 *
 *     dome(f) = ∫₀^{π/2} [1 − (1 − f)^{sec θ}] sin θ dθ
 *
 * Inverting that numerically for each preset's okta value gives the plane fraction the layer must
 * actually occupy, and reading the field's own quantile function at `1 − f` gives the threshold:
 *
 *     | preset     | oktas (dome) | plane | threshold |
 *     | ---------- | -----------: | ----: | --------: |
 *     | clear      |         0.00 | 0.000 |    (none) |
 *     | few        |         0.19 | 0.056 |     0.845 |
 *     | scattered  |         0.44 | 0.191 |     0.811 |
 *     | broken     |         0.75 | 0.484 |     0.772 |
 *     | overcast   |         1.00 | 1.000 |     0.501 |
 *
 * Which is the table below. Note how little of the range it uses between FEW and BKN, and how
 * hard it drops for OVC — an overcast has to cut *below* the whole distribution, because "no gaps"
 * is what the word means. No smooth curve fits both halves (a power law was out by a sixth of the
 * plane fraction at BKN), so the presets are interpolated directly, which is exact where it
 * matters and monotone in between.
 *
 * `updateFixed` / `updateFrame`: neither. Pure functions, evaluated per sample on the GPU and per
 * call on the CPU.
 */

import type { Node } from 'three/webgpu';
import { float, mix, smoothstep } from 'three/tsl';

/**
 * Coverage knots and the threshold each needs, from the derivation above.
 *
 * The coverage values are the METAR okta midpoints `world/weather.ts` ships; the thresholds are
 * what the shipped noise volume's own quantiles ask for. Regenerate both together if the noise
 * generator ever changes — `coverage.test.ts` is what will notice if they drift apart.
 */
export const COVERAGE_KNOTS: readonly { coverage: number; threshold: number }[] = [
  // Above the field's 98th percentile: nothing passes, so a clear sky is genuinely clear.
  { coverage: 0.0, threshold: 0.9 },
  { coverage: 0.19, threshold: 0.845 },
  { coverage: 0.44, threshold: 0.811 },
  { coverage: 0.75, threshold: 0.772 },
  // Below the field's 2nd percentile: no gaps at all, which is what overcast means.
  { coverage: 1.0, threshold: 0.5 },
];

/**
 * How much the low-frequency weather field moves the threshold about its mean.
 *
 * This is what makes cloud clump into weather rather than spreading evenly — some parts of the
 * sky busier than others at the same nominal coverage. Sized against the field: the weather slice
 * spans 0.577…0.769 about a mean of 0.667, so at 0.25 the threshold moves by ∓0.024, which is
 * half a standard deviation of the density field. Enough to be a visible difference between one
 * part of the sky and another, not enough to turn a scattered day into an overcast one somewhere.
 */
export const WEATHER_SPREAD = 0.25;

/** Mean of the weather slice, so the modulation is centred rather than biased. */
export const WEATHER_MEAN = 0.667;

/**
 * How much the weather field is allowed to move the threshold, as a function of coverage.
 *
 * Zero at both ends, and that is a bug fix rather than a nicety. At 0 oktas the modulation could
 * pull the threshold from 0.900 down to 0.875, which is *inside* the density field's upper tail —
 * so a clear sky grew wisps in the cloudiest corners of the weather map, and "clear" stopped
 * meaning clear. The same at 8 oktas, where it could push a hole into an overcast. There is
 * nothing to clump at either end: `4c(1−c)` says so and peaks where the variety belongs.
 */
function clumpiness(coverage: number): number {
  return 4 * coverage * (1 - coverage);
}

/**
 * Density threshold for a coverage and a local weather sample, on the CPU.
 *
 * The reference implementation. {@link coverageThresholdNode} is the same arithmetic on the GPU,
 * and `coverage.test.ts` holds them together.
 */
export function coverageThreshold(coverage: number, weather: number): number {
  const c = coverage < 0 ? 0 : coverage > 1 ? 1 : coverage;
  let threshold = COVERAGE_KNOTS[0].threshold;
  for (let i = 1; i < COVERAGE_KNOTS.length; i++) {
    const previous = COVERAGE_KNOTS[i - 1];
    const knot = COVERAGE_KNOTS[i];
    threshold += (knot.threshold - threshold) * smoothstepScalar(previous.coverage, knot.coverage, c);
  }
  return threshold - (weather - WEATHER_MEAN) * WEATHER_SPREAD * clumpiness(c);
}

/** The same, as a TSL node. */
export function coverageThresholdNode(
  coverage: Node<'float'>,
  weather: Node<'float'>,
): Node<'float'> {
  let threshold: Node<'float'> = float(COVERAGE_KNOTS[0].threshold);
  for (let i = 1; i < COVERAGE_KNOTS.length; i++) {
    const previous = COVERAGE_KNOTS[i - 1];
    const knot = COVERAGE_KNOTS[i];
    threshold = mix(
      threshold,
      float(knot.threshold),
      smoothstep(float(previous.coverage), float(knot.coverage), coverage),
    );
  }
  const clump = coverage.clamp(0, 1).mul(coverage.clamp(0, 1).oneMinus()).mul(4);
  return threshold.sub(weather.sub(WEATHER_MEAN).mul(WEATHER_SPREAD).mul(clump));
}

/**
 * How far above the threshold the field has to rise before the cloud is at full density.
 *
 * The second half of the same bug. The remap ran from the threshold up to **1.0**, and the field
 * never gets near 1.0 — its 98th percentile is 0.865 — so everything that survived the cut sat in
 * the bottom fifth of the remap and the cloud that was there had no substance: measured, the
 * densest five percent of columns under SCT still let 45 % of the light through. A cumulus has an
 * edge and an opaque middle, not a fifteen-percent interior.
 *
 * A tenth is about two standard deviations of the field, so a sample two σ above the threshold is
 * solid. Measured across the presets, that is what puts the ladder where the labels say:
 * clear 100 % of the sky clear, FEW 92 %, SCT 60 %, BKN 14 %, OVC 0 %.
 */
export const DENSITY_BAND = 0.1;

/**
 * How opaque the cloud that *is* there should be, for a given coverage.
 *
 * Separate from where it is, and it has to be: an overcast deck is genuinely thicker than a
 * fair-weather cumulus, but a single cumulus on a `few` day is not a wisp — it is a solid white
 * lump with a shadow under it. So this spans a modest range rather than scaling with coverage.
 */
export function coverageOpacity(coverage: number): number {
  return 0.55 + 0.45 * (coverage < 0 ? 0 : coverage > 1 ? 1 : coverage);
}

/** The same, as a TSL node. */
export function coverageOpacityNode(coverage: Node<'float'>): Node<'float'> {
  return coverage.clamp(0, 1).mul(0.45).add(0.55);
}

/** `smoothstep`, on the CPU. */
function smoothstepScalar(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}
